/**
 * 48点データ画面の初期フィルタをクエリパラメータから解決する純関数。
 *
 * 取引契約一覧 (`SupplyBilateral`) の「48点データへ」導線から遷移した際に、
 * `supply-48-data.md` AC-019 の param (date / filterArea / buysell) を初期フィルタに
 * 反映する。 値は送り側 (`SupplyBilateral/models/supply48Link.ts`) が enum 数値 /
 * ISO 日付で渡すため、 ここで parse・検証してから初期フィルタ seed に変換する。
 *
 * 対応外の param (counterparty / volumeMw 等) は 48点データ画面に対応する
 * フィルタが無いため無視する (= 受け側が解釈できる範囲だけ反映する)。
 * `contract` param はフィルタには反映しないが、 AC-S4D-052 のスクロール・
 * ハイライト対象識別子として別途 `parseSupply48ScrollTarget` が解決する
 * (= フィルタと同じ URL param を別の目的で読む。 二重に消費しても矛盾しない)。
 */
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

export type Supply48InitialFilters = {
  /** buysell param が BUY / SELL を指す場合のみ設定。 */
  buySell?: Side
  /** filterArea param が有効な AreaCode (UNSPECIFIED 以外) を指す場合のみ設定。 */
  area?: AreaCode
  /** date param が YYYY-MM-DD として解釈できる場合のみ設定 (local 0:00)。 */
  date?: Date
}

// enum 値を number として比較する (= 生の Number(raw) と enum member を直接
// 比較すると @typescript-eslint/no-unsafe-enum-comparison に触れるため number 化)。
const AREA_UNSPECIFIED: number = AreaCode.UNSPECIFIED
const BUY_SELL_VALUES: readonly number[] = [Side.BUY, Side.SELL]

/** 文字列を有効な AreaCode (UNSPECIFIED 以外) に解決する。 不正なら undefined。 */
function parseAreaCode(raw: string | null): AreaCode | undefined {
  if (raw === null || raw === '') return undefined
  const n = Number(raw)
  if (!Number.isInteger(n)) return undefined
  // 数値 enum の逆引きで定義済み値か確認 (UNSPECIFIED=0 は除外)。
  if (n === AREA_UNSPECIFIED) return undefined
  return AreaCode[n] !== undefined ? (n as AreaCode) : undefined
}

/** 文字列を BUY / SELL に解決する。 不正・UNSPECIFIED なら undefined。 */
function parseBuySell(raw: string | null): Side | undefined {
  if (raw === null || raw === '') return undefined
  const n = Number(raw)
  return BUY_SELL_VALUES.includes(n) ? (n as Side) : undefined
}

/** YYYY-MM-DD を local 0:00 の Date に解決する。 不正なら undefined。 */
function parseIsoDate(raw: string | null): Date | undefined {
  if (raw === null) return undefined
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  if (m === null) return undefined
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined
  const d = new Date(year, month - 1, day)
  // 桁溢れ (例: 2026-02-31) を弾く: 正規化後に元の値と一致するか確認。
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return undefined
  }
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * URLSearchParams から 48点データ画面の初期フィルタ seed を解決する。
 * 反映できない param は無視し、 解釈できたものだけ返す (部分適用可)。
 */
export function parseSupply48InitialFilters(
  searchParams: URLSearchParams,
): Supply48InitialFilters {
  const result: Supply48InitialFilters = {}
  const buySell = parseBuySell(searchParams.get('buysell'))
  if (buySell !== undefined) result.buySell = buySell
  const area = parseAreaCode(searchParams.get('filterArea'))
  if (area !== undefined) result.area = area
  const date = parseIsoDate(searchParams.get('date'))
  if (date !== undefined) result.date = date
  return result
}

/**
 * URLSearchParams から AC-S4D-052 のスクロール・ハイライト対象識別子 (契約の
 * 取引ID、`contract` param) を解決する。
 *
 * 識別子は既存の `contract` param (送り側 `supply48Link.ts` が publicTradeId を
 * 渡す) を再利用する。 相対取引は 1:1 モデルに統一済み
 * (leg テーブル廃止 #6547 / #6622) のため取引ID は列 (supply48SourceId) を一意に
 * 特定できる。 未指定・空文字は undefined (= 通常遷移・直接アクセスとの互換、
 * スクロール・ハイライトを行わない)。
 */
export function parseSupply48ScrollTarget(
  searchParams: URLSearchParams,
): string | undefined {
  const raw = searchParams.get('contract')
  return raw !== null && raw !== '' ? raw : undefined
}
